import type { UpdateFishGroupMetadataRequest as UpdateFishGroupMetadataRequestDto } from '@/services/types';

import type { QueryResult } from '../shared/types';

// Generated DTOs (mirror backend schema). Re-export so callers can keep all
// fish-groups imports under `@/domain/fishGroups`.
export type {
    CloseFishGroupRequest,
    CreateFishGroupRequest,
    DiscardFishGroupRequest,
} from '@/services/types';

/** A metadata correction as the API takes it: a null `notes` or population clears the value. */
export type UpdateFishGroupMetadataRequest = Omit<
    UpdateFishGroupMetadataRequestDto,
    'notes' | 'initialPopulationSize'
> & {
    notes?: string | null;
    initialPopulationSize?: number | null;
};

/** The fish group in a cage now, with the fields the cage settings can correct. */
export interface CageActiveFishGroup {
    id: number;
    name: string;
    initialPopulationSize: number | null;
}

/** The cage settings' correction: a metadata correction without the notes. */
export type UpdateCageActiveFishGroupRequest = Omit<UpdateFishGroupMetadataRequest, 'notes'>;

export type {
    CageFishGroupHistoryItemDto,
    ClientFishGroupsOverviewDto,
    FishGroupCurrentLocationDto,
    FishGroupDetailDto,
    FishGroupDto,
    FishGroupLineageDto,
    FishGroupLocationHistoryItemDto,
    FishGroupOperationSummaryDto,
    FishGroupTransferDto,
    FishGroupTransferEventDto,
    LineageEdgeDto,
    LineageFlatDto,
    LineageFlatEntryDto,
    LineageGraphDto,
    LineageNodeDto,
    OverviewCageDto,
    OverviewFishGroupSummaryDto,
    OverviewLocationDto,
} from '@/services/types';
export { CloseReason, DiscardReason } from '@/services/types';

// ---------------------------------------------------------------------------
// Status enums — backend ships these as inlined string literal unions.
// Define here as named types so the domain can reuse them in narrow guards.
// ---------------------------------------------------------------------------

export type FishGroupStatus =
    | 'pending'
    | 'active'
    | 'closed'
    | 'discarded'
    | 'harvested'
    | 'merged'
    | 'split';

export type OverviewFishGroupStatus = 'active' | 'closed' | 'discarded';

export type FishGroupOperationType =
    | 'creation'
    | 'relocation'
    | 'split'
    | 'merge'
    | 'split_and_merge'
    | 'redistribution'
    | 'closure'
    | 'discard'
    | 'revert';

export type FishGroupEventType =
    | 'created'
    | 'closed'
    | 'relocated'
    | 'discarded'
    // Still produced by historical rows and still accepted by the API, but no
    // longer offered when recording a new event — see
    // FISH_GROUP_MANUAL_EVENT_TYPES.
    | 'mortality'
    | 'harvest'
    | 'measurement'
    | 'feeding'
    | 'treatment'
    | 'delousing'
    | 'metadata_updated'
    // Written on each group a revert restores.
    | 'reverted';

/**
 * Event types the operator can record from the UI.
 *
 * Reduced to delousing + partial harvest: offering five vague options caused
 * confusion, and the PRFAQ only names delousing (partial harvest stays because
 * it is part of the action matrix). `mortality`, `measurement`, `feeding` and
 * `treatment` are NOT removed — the backend still accepts them and existing
 * rows still use them, so they remain in `FishGroupEventType` and render
 * correctly in history. They are simply not offered for new events, and can be
 * added back here if users ask for them.
 */
export const FISH_GROUP_MANUAL_EVENT_TYPES = [
    'delousing',
    'harvest',
] as const satisfies readonly FishGroupEventType[];

/** The event types `recordEvent` may be called with from the UI. */
export type FishGroupManualEventType = (typeof FISH_GROUP_MANUAL_EVENT_TYPES)[number];

/**
 * Event types that record a correction to the fish group's own record rather
 * than something that happened to the fish. Written only by the metadata
 * endpoint, and rendered as a field-level diff rather than a raw payload dump.
 */
export const FISH_GROUP_CORRECTION_EVENT_TYPES = [
    'metadata_updated',
] as const satisfies readonly FishGroupEventType[];

/** Fields correctable through the metadata endpoint. */
export type EditableFishGroupField = 'name' | 'notes' | 'initialPopulationSize';

/**
 * A single field-level change, as recorded in a `metadata_updated` event's
 * payload. `from`/`to` are the raw stored values, so an old entry stays legible
 * even after the field changes again later.
 */
export interface FishGroupMetadataChange {
    field: EditableFishGroupField;
    from: string | number | null;
    to: string | number | null;
}

// ---------------------------------------------------------------------------
// Overview domain (used by FishGroupsOverview / LocationFishGroupsTable)
// ---------------------------------------------------------------------------

export interface OverviewFishGroupSummary {
    id: number;
    name: string;
    status: OverviewFishGroupStatus;
    lifecycleStartedAt: string | null;
    /**
     * Start of this group's stay in the cage. This — not `lifecycleStartedAt` — is
     * the date that bounds the cage's data: measurements are keyed by (cage, day),
     * so a group that was relocated has one interval per cage and only the days
     * since its arrival belong here.
     */
    fromDate: string;
}

export interface OverviewCage {
    cageId: number;
    cageName: string;
    activeBioscopes: number[];
    activeFishGroup: OverviewFishGroupSummary | null;
}

export interface OverviewLocation {
    locationId: number;
    locationName: string;
    activeCages: OverviewCage[];
    inactiveCages: OverviewCage[];
    cages: OverviewCage[];
}

export interface ClientFishGroupsOverview {
    clientId: number;
    activeLocations: OverviewLocation[];
    inactiveLocations: OverviewLocation[];
}

export type UseGetClientFishGroupsOverviewResult = QueryResult<ClientFishGroupsOverview | null>;

// ---------------------------------------------------------------------------
// Detail view — `GET /:clientId/:fishGroupId`
// ---------------------------------------------------------------------------

export interface FishGroupCurrentLocation {
    locationRowId: number;
    cageId: number;
    cageName: string;
    locationId: number;
    locationName: string;
    fromDate: string;
}

export interface FishGroup {
    id: number;
    name: string;
    status: FishGroupStatus;
    clientId: number;
    fishTypeId: number;
    initialPopulationSize: number | null;
    lifecycleStartedAt: string | null;
    lifecycleEndedAt: string | null;
    notes: string | null;
}

export interface FishGroupDetail extends FishGroup {
    currentLocation: FishGroupCurrentLocation | null;
}

export type UseGetFishGroupDetailResult = QueryResult<FishGroupDetail | null>;
